import type { RemoteMessage, SongSummary } from '@pipi/shared';
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ChevronLeft,
  ListMusic,
  LoaderCircle,
  Mic2,
  Pause,
  Play,
  Search,
  Send,
  Smartphone,
  Trash2,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CoverArt } from '../components/cover-art';
import { api, websocketUrl } from '../lib/api';
import { useAppState } from '../state/app-state';

type Tab = 'control' | 'songs' | 'queue';

export function RemotePage() {
  const { data, refresh, addToQueue, removeFromQueue } = useAppState();
  const [tab, setTab] = useState<Tab>('control');
  const [connected, setConnected] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SongSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const pin = useMemo(() => new URLSearchParams(window.location.search).get('pin') || '', []);

  useEffect(() => {
    const socket = new WebSocket(websocketUrl());
    socketRef.current = socket;
    socket.addEventListener('open', () => socket.send(JSON.stringify({ type: 'hello', payload: { role: 'remote', pin }, sender: 'remote' } satisfies RemoteMessage)));
    socket.addEventListener('message', (event) => {
      try {
        const message = JSON.parse(String(event.data)) as RemoteMessage;
        if (message.type === 'hello') setConnected(true);
        if (message.payload.error === 'PIN_ERROR') setPinError(true);
        if (message.type === 'state' && message.payload.queue) void refresh(true);
      } catch {
        // Ignore malformed packets.
      }
    });
    socket.addEventListener('close', (event) => {
      setConnected(false);
      if (event.code === 4001) setPinError(true);
    });
    return () => socket.close();
  }, [pin, refresh]);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = window.setTimeout(() => {
      setSearching(true);
      void api.catalog({ q: query, capability: 'playable', limit: 30 }).then((page) => setResults(page.items)).finally(() => setSearching(false));
    }, 240);
    return () => window.clearTimeout(timer);
  }, [query]);

  const send = (type: RemoteMessage['type'], payload: Record<string, unknown>) => {
    const message: RemoteMessage = { type, payload, sender: 'remote' };
    if (socketRef.current?.readyState === WebSocket.OPEN) socketRef.current.send(JSON.stringify(message));
    else void fetch('/api/remote/command', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...message, pin }) });
  };

  if (pinError) {
    return <main className="remote-page remote-error"><img src="/brand/pipi-karaoke-mascot.png" alt="" /><h1>房间 PIN 不正确</h1><p>请重新扫描电视上的二维码。</p></main>;
  }

  return (
    <main className="remote-page">
      <header className="remote-header">
        <div><img src="/brand/pipi-karaoke-mascot.png" alt="" /><span><strong>皮皮 K 歌</strong><small>手机点歌遥控</small></span></div>
        <span className={connected ? 'remote-connection is-online' : 'remote-connection'}>{connected ? <Wifi /> : <WifiOff />}{connected ? '已连接电视' : '正在连接'}</span>
      </header>

      <div className="remote-content">
        {tab === 'control' && (
          <section className="remote-control">
            <div className="remote-now-playing">
              <span className="eyebrow">NOW PLAYING</span>
              {data?.queue[0] ? <><CoverArt song={data.queue[0].song} /><div><h2>{data.queue[0].song.title}</h2><p>{data.queue[0].song.artist}</p></div></> : <div className="remote-now-playing__empty"><Mic2 /><span>电视正在等待点歌</span></div>}
            </div>
            <div className="remote-transport">
              <button type="button" aria-label="后退" onClick={() => send('key', { key: 'Escape' })}><ChevronLeft /></button>
              <button type="button" aria-label="播放" onClick={() => send('player:command', { command: 'play' })}><Play fill="currentColor" /></button>
              <button type="button" aria-label="暂停" onClick={() => send('player:command', { command: 'pause' })}><Pause fill="currentColor" /></button>
              <button type="button" aria-label="快进" onClick={() => send('player:command', { command: 'seek-forward' })}><ArrowRight /></button>
            </div>
            <div className="remote-dpad">
              <button type="button" className="remote-dpad__up" aria-label="上" onClick={() => send('key', { key: 'ArrowUp' })}><ArrowUp /></button>
              <button type="button" className="remote-dpad__left" aria-label="左" onClick={() => send('key', { key: 'ArrowLeft' })}><ArrowLeft /></button>
              <button type="button" className="remote-dpad__ok" aria-label="确认" onClick={() => send('key', { key: 'Enter' })}>OK</button>
              <button type="button" className="remote-dpad__right" aria-label="右" onClick={() => send('key', { key: 'ArrowRight' })}><ArrowRight /></button>
              <button type="button" className="remote-dpad__down" aria-label="下" onClick={() => send('key', { key: 'ArrowDown' })}><ArrowDown /></button>
            </div>
            <p className="remote-pin"><Smartphone />房间 PIN <strong>{pin || data?.remote.pin || '—'}</strong></p>
          </section>
        )}

        {tab === 'songs' && (
          <section className="remote-songs">
            <label><Search /><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜歌名或歌手" /></label>
            {searching && <div className="remote-loading"><LoaderCircle className="spin" />搜索中</div>}
            {!searching && query && !results.length && <p className="remote-empty">没有找到，换个关键词试试</p>}
            <div className="remote-song-list">
              {results.map((song) => (
                <article key={song.id}><CoverArt song={song} /><div><h3>{song.title}</h3><p>{song.artist}</p></div><button type="button" onClick={() => void addToQueue(song).then(() => refresh(true))}><Send />点歌</button></article>
              ))}
            </div>
          </section>
        )}

        {tab === 'queue' && (
          <section className="remote-queue">
            <header><h2>点歌台</h2><span>{data?.queue.length ?? 0} 首</span></header>
            {!data?.queue.length && <p className="remote-empty">还没有歌曲，先去点一首吧</p>}
            {data?.queue.map((item, index) => (
              <article key={item.id}><strong>{index + 1}</strong><CoverArt song={item.song} /><div><h3>{item.song.title}</h3><p>{item.singer} · {item.song.artist}</p></div><button type="button" aria-label="移除" onClick={() => void removeFromQueue(item.id)}><Trash2 /></button></article>
            ))}
          </section>
        )}
      </div>

      <nav className="remote-tabs">
        <button type="button" className={tab === 'control' ? 'is-active' : ''} onClick={() => setTab('control')}><Smartphone />遥控</button>
        <button type="button" className={tab === 'songs' ? 'is-active' : ''} onClick={() => setTab('songs')}><Search />点歌</button>
        <button type="button" className={tab === 'queue' ? 'is-active' : ''} onClick={() => setTab('queue')}><ListMusic />队列{Boolean(data?.queue.length) && <b>{data?.queue.length}</b>}</button>
      </nav>
    </main>
  );
}
